import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ErrorHandler,
  Injectable,
  Injector,
  NgZone,
  Provider,
  inject
} from '@angular/core';

import { ToastService } from 'src/app/ui/components/toast';
import { environment } from 'src/environments/environment';

import { LivPortfolioErrorResponse } from '../../models/liv-portfolio-response-protocol.model';
import { ErrorService } from '../services/error.service';
import { LoggerService } from '../services/logger.service';

function isLivPortfolioErrorResponse(
  error: Error | LivPortfolioErrorResponse
): error is LivPortfolioErrorResponse {
  const livErrorResponse = error as LivPortfolioErrorResponse;

  return (
    Boolean(livErrorResponse?.status) &&
    Boolean(livErrorResponse?.error?.message)
  );
}

type HandledError = {
  message: string;
  stackTrace: string;
  isHttpError: boolean;
  status: number;
  url: string;
};

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly errorService = inject(ErrorService);
  private readonly loggerService = inject(LoggerService);
  private readonly alertDomains = this.extractDomainsFromEnvironment();

  handleError(
    error: Error | LivPortfolioErrorResponse | HttpErrorResponse
  ): void {
    const handled = this.describe(error);

    this.zone.run(() => {
      this.loggerService.logError(handled.message, handled.stackTrace);

      if (!handled.isHttpError || !this.shouldShowAlert(handled)) {
        return;
      }

      // Resolve toast lazily to avoid DI cycles during app bootstrap.
      const toast = this.injector.get(ToastService);
      toast.danger(handled.message);
      this.loggerService.sendErrorToServer(handled.message);
    });
  }

  private describe(
    error: Error | LivPortfolioErrorResponse | HttpErrorResponse
  ): HandledError {
    if (error instanceof HttpErrorResponse) {
      return {
        message: this.errorService.getServerMessage(error),
        stackTrace: this.errorService.getServerStack(error),
        isHttpError: true,
        status: error.status,
        url: error.url ?? ''
      };
    }

    if (isLivPortfolioErrorResponse(error)) {
      return {
        message: this.errorService.getServerMessage(error),
        stackTrace: this.errorService.getServerStack(error),
        isHttpError: true,
        status: error.status,
        url: error.error?.details?.url ?? ''
      };
    }

    return {
      message: this.errorService.getClientMessage(error),
      stackTrace: this.errorService.getClientStack(error),
      isHttpError: false,
      status: 0,
      url: ''
    };
  }

  private shouldShowAlert({ status, url }: HandledError): boolean {
    // 401 já leva à página de sessão expirada no ResponseProtocolInterceptor.
    if ((status as HttpStatusCode) === HttpStatusCode.Unauthorized) {
      return false;
    }

    if (!url) {
      // Envelope sem URL ainda é erro da API tratado pelo interceptor
      return true;
    }

    try {
      const domain = new URL(url).hostname;
      const matches = this.alertDomains.some(
        (alertDomain) => domain === alertDomain || domain.endsWith(alertDomain)
      );

      if (!matches) {
        console.warn(
          `Error from ${url} does not match configured alert domains. No alert will be shown.`
        );
      }

      return matches;
    } catch {
      return false;
    }
  }

  private extractDomainsFromEnvironment(): string[] {
    return [environment.apiPortfolio, environment.apiLiv]
      .filter(Boolean)
      .map((url) => url.replace(/(https?:\/\/)?([^/:]+).*/i, '$2'));
  }
}

export function provideGlobalErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useExisting: GlobalErrorHandlerService
  };
}
